import { landmineNames } from '@mm/constants/landmine-names';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';

export const NATIVE_INTEGRATIONS = ['googleSheets', 'mailchimp'] as const;
export type NativeIntegration = typeof NATIVE_INTEGRATIONS[number];

export const useConnectNativeIntegration = () => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const { isFreeTrialPromotionActive } =
    useFreeTrialUpgradeModal(activeFacebookPage);
  const hasNativeIntegrationsFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const { getOauthUrl } = useGoogleSheetsCRUDActions();
  const { getMailchimpOauthUrl } = useMailchimpCRUDActions();
  const modalActions = useModalActions();

  const connectIntegration = useMemo(
    () => async (type, onSuccessBilling?: () => void) => {
      if (!hasNativeIntegrationsFlag && !isFreeTrialPromotionActive) {
        modalActions.openUniversalUpgradeLandmineModal({
          feature: 'nativeIntegrations',
          landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
          onSuccessBilling,
        });
        return;
      }
      const link =
        type === 'googleSheets'
          ? await getOauthUrl(window.location.href)
          : await getMailchimpOauthUrl(window.location.href);
      if (link) {
        window.open(link, '_self');
      }
    },
    [
      hasNativeIntegrationsFlag,
      isFreeTrialPromotionActive,
      modalActions,
      getOauthUrl,
      getMailchimpOauthUrl,
    ],
  );

  const connectNativeIntegration = useMemo(
    () => (type: NativeIntegration) => {
      switch (type) {
        case 'googleSheets':
          connectIntegration(type);
          break;
        case 'mailchimp':
          connectIntegration(type);
          break;
        default:
          break;
      }
    },
    [connectIntegration],
  );

  return {
    connectNativeIntegration,
  };
};
